import {
  makeDataState,
  makeListState,
  concatUnique,
  STATUSES,
} from 'app/utils/redux';

import { AD_TYPES, AD_STATUSES, AUTH_STATUSES } from 'app/utils/constants';

import snakeize from 'snakeize';

import * as t from './types';

const initialState = {
  firebaseIdToken: null,
  isLoading: false,
  user: makeDataState(),
  platform: null,
  version: null,
  sessionId: null,
  isPushEnabled: false,
  isStorageWarningShow: false,
  abTest: {
    isDataReceived: null,
  },
  adLoadStatus: {
    [AD_TYPES.REWARD]: AD_STATUSES.LOADING,
    [AD_TYPES.CHEER_REWARD]: AD_STATUSES.LOADING,
    [AD_TYPES.INTERSTITIAL]: AD_STATUSES.LOADING,
    [AD_TYPES.INTERSTITIAL_BACKFILL]: AD_STATUSES.LOADING,
    [AD_TYPES.FBINTERSTITIAL]: AD_STATUSES.LOADING,
  },
  isLimitedWeb: false,
  promotions: {
    dailyBalloonPromotion: {},
  },
  showAdTapCount: 200,
  episodeAdOrder: 'mopub_ad_first',
  induceOfferwallType: 'tnk',
  currentAdMeta: {},
  source: null,
  userMarketItems: {}, // userId is the key and dataState is value
  userFans: {},
  userSettings: {}, // objectType-objectId is the first depth key, and key is second depth key
  storyAd: {
    ...makeDataState(),
    visited: [],
  },
  storyAdFull: {
    ...makeDataState(),
    isViewed: false,
  },
  latestMyStory: makeDataState(),
  vouchers: {}, // voucherType as key, list state
  recommendationVoucher: makeDataState(),
  donateReactionInfo: makeDataState(),
  recoveryUsers: makeListState(),
  socialProviders: makeListState(),
  certificationError: null,
  authStatus: AUTH_STATUSES.LOADING,
  isMaintenance: false,
  maintenanceExtraText: null,
  testBulkPurchaseCount: 5,
};

export default (state = initialState, action) => {
  switch (action.type) {
    // social login or fetch user info
    case `${t.FETCH_USER_INFO}_PENDING`:
      return {
        ...state,
        user: {
          ...state.user,
          status: STATUSES.PENDING,
        },
      };

    case `${t.FETCH_USER_INFO}_SUCCESS`:
      return {
        ...state,
        firebaseIdToken: action.meta.saveToken
          ? action.meta.firebaseIdToken || state.firebaseIdToken
          : state.firebaseIdToken,
        user: {
          ...state.user,
          status: STATUSES.SUCCESS,
          isLoaded: true,
          data: {
            ...state.user.data,
            ...action.payload.result,
            ageGrade: 19,
          },
        },
        authStatus: AUTH_STATUSES.SUCCESS,
      };

    case `${t.FETCH_USER_INFO}_ERROR`:
      return {
        ...state,
        user: {
          ...state.user,
          status: STATUSES.ERROR,
        },
        authStatus: AUTH_STATUSES.FAILED,
      };

    // change nickname
    case `${t.CHANGE_NICKNAME}_SUCCESS`:
      return {
        ...state,
        user: {
          ...state.user,
          status: STATUSES.SUCCESS,
          isLoaded: true,
          data: {
            ...action.payload.result,
            ageGrade: 19,
          },
        },
      };
    case `${t.CHANGE_NICKNAME}_PENDING`:
      return {
        ...state,
        user: {
          ...state.user,
          status: STATUSES.PENDING,
        },
      };
    case `${t.CHANGE_NICKNAME}_ERROR`:
      return {
        ...state,
        user: {
          ...state.user,
          status: STATUSES.ERROR,
        },
      };

    // update me
    case t.UPDATE_ME:
      return {
        ...state,
        user: {
          ...state.user,
          data: {
            ...state.user.data,
            ...action.meta.user,
            ageGrade: 19,
          },
        },
      };

    // signout & unregister
    case `${t.SIGN_OUT}_SUCCESS`:
    case `${t.UNREGISTER_USER}_SUCCESS`:
      return {
        ...state,
        user: makeDataState(),
        firebaseIdToken: null,
        authStatus: AUTH_STATUSES.LOADING,
      };

    // reset user
    case t.RESET_USER:
      return {
        ...state,
        firebaseIdToken: null,
        user: action.retry ? state.user : makeDataState(),
      };

    // toggle storage warnging
    case t.SHOW_STORAGE_WARNING:
      return {
        ...state,
        isStorageWarningShow: true,
      };

    case t.HIDE_STORAGE_WARNING:
      return {
        ...state,
        isStorageWarningShow: false,
      };

    // ab test
    case t.SET_EXPERIMENTS:
      return {
        ...state,
        abTest: {
          isDataReceived: true,
          ...action.data,
        },
      };

    case t.UPDATE_AB_TEST_DATA_RECEIVED:
      return {
        ...state,
        abTest: {
          ...state.abTest,
          isDataReceived: action.isDataReceived,
        },
      };

    case t.SET_SHOW_AD_TAP_COUNT:
      return {
        ...state,
        showAdTapCount: action.tapCount,
      };
    // adMob
    case t.SET_AD_LOAD_STATUS:
      return {
        ...state,
        adLoadStatus: {
          ...state.adLoadStatus,
          [action.adType]: action.status,
        },
      };

    // promotion
    case `${t.CHECK_PROMOTIONS}_SUCCESS`: {
      const { dailyBalloonPromotion } = action.payload.result;
      return {
        ...state,
        promotions: {
          dailyBalloonPromotion,
        },
      };
    }

    case t.SET_PROMOTIONS:
      return {
        ...state,
        promotions: {
          ...state.promotions,
          ...action.promotions,
        },
      };

    // fetch user market items
    case `${t.FETCH_USERS_MARKET_ITEMS}_PENDING`: {
      const { userIds, forceFetch } = action.meta;
      const newUserMarketItems = userIds.reduce((acc, value) => {
        if (state.userMarketItems[value] && !forceFetch) {
          return acc;
        }

        return {
          ...acc,
          [value]: makeListState(),
        };
      }, {});

      return {
        ...state,
        userMarketItems: {
          ...state.userMarketItems,
          ...newUserMarketItems,
        },
      };
    }

    case `${t.FETCH_USERS_MARKET_ITEMS}_SUCCESS`: {
      const updatedUserMarketItems = action.payload.result.reduce(
        (acc, value) => {
          if (
            state.userMarketItems[value.id].isLoaded &&
            !action.meta.forceFetch
          ) {
            return acc;
          }

          return {
            ...acc,
            [value.id]: {
              ...state.userMarketItems[value.id],
              isLoaded: true,
              list: value.marketItems,
            },
          };
        },
        {}
      );

      return {
        ...state,
        userMarketItems: {
          ...state.userMarketItems,
          ...updatedUserMarketItems,
        },
      };
    }

    // fetch user fans
    case `${t.FETCH_USER_FANS}_PENDING`: {
      const { userIds, targetUserId } = action.meta;
      const newUserFans = userIds.reduce((acc, value) => {
        if (state.userFans[value]) {
          return acc;
        }

        return {
          ...acc,
          [`${value}:${targetUserId}`]: makeDataState(),
        };
      }, {});

      return {
        ...state,
        userFans: {
          ...state.userFans,
          ...newUserFans,
        },
      };
    }

    case `${t.FETCH_USER_FANS}_SUCCESS`: {
      const { targetUserId } = action.meta;
      const { fanClub, users } = action.payload.result;
      const updatedUserFans = users.reduce((acc, value) => {
        const { id, isFan } = value;
        const key = `${id}:${targetUserId}`;

        return {
          ...acc,
          [key]: {
            ...state.userFans[key],
            isLoaded: true,
            data: { isFan, fanClub },
          },
        };
      }, {});

      return {
        ...state,
        userFans: {
          ...state.userFans,
          ...updatedUserFans,
        },
      };
    }

    // user settings
    case `${t.FETCH_USER_SETTING}_SUCCESS`: {
      const { objectType, objectId } = action.meta;
      const key = `${objectType}:${objectId}`;

      return {
        ...state,
        userSettings: {
          ...state.userSettings,
          [key]: snakeize(action.payload.result.setting),
        },
      };
    }

    case `${t.SET_USER_SETTING}_SUCCESS`: {
      const { objectType, objectId } = action.meta;
      const key = `${objectType}:${objectId}`;
      return {
        ...state,
        userSettings: {
          ...state.userSettings,
          [key]: {
            ...state.userSettings[key],
            ...snakeize(action.payload.result.setting),
          },
        },
      };
    }

    // ad story list
    case `${t.FETCH_STORY_AD}_SUCCESS`:
      return {
        ...state,
        storyAd: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          data: action.payload.result,
        },
      };

    // ad story full
    case t.SET_STORY_AD_FULL:
      return {
        ...state,
        storyAdFull: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          data: action.data,
          isViewed: false,
        },
      };

    case t.VIEW_STORY_AD_FULL:
      return {
        ...state,
        storyAdFull: {
          ...state.storyAdFull,
          isViewed: true,
        },
      };

    case t.RESET_STORY_AD_FULL:
      return {
        ...state,
        storyAdFull: {
          ...makeDataState(),
          isViewed: false,
        },
      };

    case `${t.FETCH_LATEST_MY_STORY}_SUCCESS`:
      return {
        ...state,
        latestMyStory: {
          isLoaded: true,
          status: STATUSES.SUCCESS,
          data: action.payload.result,
        },
      };

    // set value
    case t.SET_VALUE:
      return {
        ...state,
        [action.key]: action.value,
      };

    case t.SET_LOADING:
      return {
        ...state,
        isLoading: action.isLoading,
      };

    case `${t.FETCH_VOUCHERS}_PENDING`:
      return {
        ...state,
        vouchers: {
          ...state.vouchers,
          [action.meta.voucherType]: {
            status: STATUSES.PENDING,
            isLoaded: false,
            list: makeListState(),
          },
        },
      };
    case `${t.FETCH_VOUCHERS}_SUCCESS`: {
      return {
        ...state,
        vouchers: {
          ...state.vouchers,
          [action.meta.voucherType]: {
            status: STATUSES.SUCCESS,
            isLoaded: true,
            list: action.payload.result,
          },
        },
      };
    }

    case `${t.FETCH_VOUCHER_RECOMMENDATION}_SUCCESS`:
      return {
        ...state,
        recommendationVoucher: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          data: action.payload.result,
        },
      };

    case `${t.FETCH_VOUCHER_RECOMMENDATION}_ERROR`:
      return {
        ...state,
        recommendationVoucher: {
          status: STATUSES.ERROR,
          isLoaded: true,
          data: {},
        },
      };

    case `${t.POST_VOUCHER}_SUCCESS`:
      return {
        ...state,
        vouchers: {
          ...state.vouchers,
          [action.meta.voucherType]: {
            status: STATUSES.SUCCESS,
            isLoaded: true,
            list: state.vouchers[action.meta.voucherType].list.map((item) => {
              if (item.id === action.meta.id) {
                return {
                  ...item,
                  isUsed: true,
                };
              }

              return item;
            }),
          },
        },
      };

    case t.USED_UP_VOUCHER:
      return {
        ...state,
        vouchers: {
          ...state.vouchers,
          [action.meta.voucherType]: {
            status: STATUSES.SUCCESS,
            isLoaded: true,
            list: state.vouchers[action.meta.voucherType].list.map((item) => {
              if (item.id === action.meta.id) {
                return {
                  ...item,
                  isUsed: true,
                };
              }

              return item;
            }),
          },
        },
      };

    case `${t.FETCH_DONATE_POPUP}_SUCCESS`:
      return {
        ...state,
        donateReactionInfo: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          data: action.payload.result,
        },
      };

    case `${t.POST_UNIVERSAL_BALLOON}_SUCCESS`: {
      const { balloonDetail } = action.payload.result;
      const totalBalloon = Object.values(balloonDetail).reduce(
        (a, b) => a + b,
        0
      );
      return {
        ...state,
        user: {
          ...state.user,
          data: {
            ...state.user.data,
            balloon: totalBalloon,
            balloonDetail: action.payload.result.balloonDetail,
          },
        },
      };
    }
    case `${t.FETCH_MY_SOCIAL}_SUCCESS`:
      return {
        ...state,
        socialProviders: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          list: action.payload.result.socialProviders,
        },
      };
    case `${t.FETCH_MY_PHONE}_SUCCESS`:
      return {
        ...state,
        user: {
          ...state.user,
          data: {
            ...state.user.data,
            phoneNumber: action.payload.result.phoneNumber,
          },
        },
      };
    case `${t.FETCH_MY_AGE_GRADE}_SUCCESS`: {
      return {
        ...state,
        user: {
          ...state.user,
          data: {
            ...state.user.data,
            ageGrade: 19,
            gender: action.payload.result.gender,
            ageGradeExpired: false,
          },
        },
      };
    }

    case `${t.FETCH_MY_AGE_GRADE}_ERROR`: {
      const { result } = action.payload.error;
      return {
        ...state,
        certificationError: result ? result.code : null,
      };
    }

    case `${t.POST_MY_CERTIFICATION}_SUCCESS`: {
      if (action.meta.type === 'AC') {
        localStorage.removeItem('certificationExpired');
      }
      return {
        ...state,
        user: {
          ...state.user,
          data: {
            ...state.user.data,
            ageGrade: 19,
            ageGradeExpired: false,
            phoneNumber:
              action.meta.type === 'PL'
                ? action.payload.result.phoneNumber
                : state.user.data.phoneNumber,
          },
        },
      };
    }

    case `${t.POST_MY_SOCIAL_CONNECT}_SUCCESS`:
      return {
        ...state,
        socialProviders: {
          ...state.socialProviders,
          list: state.socialProviders.list.concat(action.meta.social),
        },
      };

    case `${t.POST_MY_SOCIAL_DISCONNECT}_SUCCESS`:
      return {
        ...state,
        socialProviders: {
          ...state.socialProviders,
          list: state.socialProviders.list.filter(
            (social) => social !== action.meta.social
          ),
        },
      };

    case `${t.RECOVERY_USER_BY_PHONE}_SUCCESS`:
      return {
        ...state,
        recoveryUsers: {
          ...state.recoveryUsers,
          status: STATUSES.SUCCESS,
          isLoaded: true,
          list: action.payload.result.users,
        },
      };

    default:
      return { ...state };
  }
};
export { initialState };



// WEBPACK FOOTER //
// ./src/app/redux/common/reducer.js